import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDebouncedCallback } from "use-debounce";
import { FloatButton, message, Pagination, Tooltip, Typography } from "antd";
import type { TablePaginationConfig } from "antd/es/table";

import { useAppDispatch } from "redux/hooks";

import Table from "components/ui-component/Table";

import { useAuth0 } from "@auth0/auth0-react";
import {
  deleteCoverPage,
  getCoverPages,
  useCoverPages,
} from "redux/slice/coverPageSlice";
import AddCoverPageModal from "components/coverPage/AddCoverPageModal";
import EditCoverPageModal from "components/coverPage/EditCoverPageModal";
import AssignedUsersModal from "components/coverPage/AssignedUsersModal";
import AssignedNumbersModal from "components/coverPage/AssignedNumbersModal";
import { HeaderActions, AssignedCount } from "./CoverPage.style";
import { Button } from "components/ui-component";
import AddIconCollapseIcon from "components/Icons/AddIconCollapse";
import { ConfirmationModal } from "components/ui-component/modals";
import EditDropIcon from "components/Icons/EditDropIcon";
import DeleteThinIcon from "components/Icons/DeleteThinIcon";
import CoverPageTableCard from "components/CoverPageTableCard/CoverPageTableCard";
import { CoverPageType } from "../../redux/slice/coverPageSlice";
import { FaxPageHeader, FaxPageStyle } from "components/fax/SecureFax.style";
import SinglePlusIcon from "components/Icons/SinglePlusIcon";
import { onGetUserInfo } from "components/Helper/onGetUserInfo";

interface coverPageProps {
  contactfilter: any;
  setContactFilter: (filter: any) => void;
}

const CoverPage = (props: coverPageProps) => {
  const { contactfilter, setContactFilter } = props;
  const { user } = useAuth0();
  const navigation = useNavigate();
  const coverPageList = useCoverPages();

  const [isLoading, seIsLoading] = useState<boolean>(false);
  const [deleteId, setDeleteId] = useState<any>(null);
  const [isAddModalVisible, setIsAddModalVisible] = useState<boolean>(false);
  const [selectedCoverPage, setSelectedCoverPage] = useState<any>(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState<boolean>(false);
  const [isAssignedUsersModalVisible, setIsAssignedUsersModalVisible] =
    useState<boolean>(false);
  const [selectedAssignedUsers, setSelectedAssignedUsers] = useState<any[]>([]);
  const [isAssignedNumbersModalVisible, setIsAssignedNumbersModalVisible] =
    useState<boolean>(false);
  const [selectedAssignedNumbers, setSelectedAssignedNumbers] = useState<any[]>(
    []
  );

  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);

  const dispatch = useAppDispatch();

  useEffect(() => {
    onCoverPageListGet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCoverPageListGet = useDebouncedCallback(async () => {
    onGetUserInfo(dispatch, user, navigation);
    seIsLoading(true);
    try {
      const params = {
        ...contactfilter,
      };
      await dispatch(getCoverPages(params)).unwrap();
      seIsLoading(false);
    } catch (error: any) {
      seIsLoading(false);
      console.log("contact list get error -->", error?.message);
    }
  }, 500);

  const openAssignedUsersModal = (row: CoverPageType) => {
    setSelectedAssignedUsers(row.assigned_users || []);
    setIsAssignedUsersModalVisible(true);
    setSelectedCoverPage(row);
  };

  const openAssignedNumbersModal = (row: CoverPageType) => {
    setSelectedAssignedNumbers(row.assigned_numbers || []);
    setIsAssignedNumbersModalVisible(true);
    setSelectedCoverPage(row);
  };

  const editCoverPage = (row: CoverPageType) => {
    setSelectedCoverPage(row);
    setIsEditModalVisible(true);
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 500,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 500,
    },
    {
      title: "Assigned Users",
      key: "assigned_users",
      width: 180,
      render: (_: unknown, row: any) => (
        <AssignedCount
          onClick={(e) => {
            e.stopPropagation();
            openAssignedUsersModal(row);
          }}
        >
          {row.assigned_users?.length ?? 0}
        </AssignedCount>
      ),
    },
    {
      title: "Assigned Numbers",
      key: "assigned_numbers",
      width: 180,
      render: (_: unknown, row: any) => (
        <AssignedCount
          onClick={(e) => {
            e.stopPropagation();
            openAssignedNumbersModal(row);
          }}
        >
          {row.assigned_numbers?.length ?? 0}
        </AssignedCount>
      ),
    },
    {
      title: "",
      key: "action",
      width: 100,
      fixed: "right" as const,
      render: (_: any, record: any) => (
        <div className="action-wrapper">
          <div className="securefax-action">
            <Button
              type="text"
              className="securefax-action__list"
              onClick={(e) => {
                e.stopPropagation();
                editCoverPage(record);
              }}
              icon={
                <Tooltip title="Edit Cover Page">
                  <span>
                    <EditDropIcon />
                  </span>
                </Tooltip>
              }
            />
            <Button
              type="text"
              className="securefax-action__list"
              onClick={(e) => {
                e.stopPropagation();
                setDeleteId(record?.id);
              }}
              icon={
                <Tooltip title="Delete Cover Page">
                  <span>
                    <DeleteThinIcon width="14" height="14" strokeWidth="2" />
                  </span>
                </Tooltip>
              }
            />
          </div>
        </div>
      ),
    },
  ];

  const handlePageChange = (pagination: TablePaginationConfig) => {
    setContactFilter({
      ...contactfilter,
      page: pagination.current ?? 1,
      per_page: pagination.pageSize ?? 10,
    });
  };

  const onDeleteCoverPage = async (id: number) => {
    setDeleteLoading(true);
    try {
      await dispatch(deleteCoverPage(id)).unwrap();
      setDeleteId(null);
      message.success("Cover Page deleted successfully");
      onCoverPageListGet();
    } catch (error: any) {
      console.log("Delete Cover Page Err: --->", error?.message);
      message.error("Failed to delete cover page");
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <FaxPageStyle>
      <FaxPageHeader>
        <Typography.Title level={5}>Cover Page</Typography.Title>
        <HeaderActions>
          <div className="mobile-hide">
            <Button
              size="small"
              type="primary"
              onClick={() => setIsAddModalVisible(true)}
              icon={<AddIconCollapseIcon width="20" height="20" />}
            >
              Add Coverpage
            </Button>
          </div>
          <div className="desktop-hide">
            <FloatButton
              icon={<SinglePlusIcon />}
              onClick={() => setIsAddModalVisible(true)}
            />
          </div>
        </HeaderActions>
      </FaxPageHeader>
      <div className="mobile-table-block desktop-hide">
        <CoverPageTableCard
          coverPageCardData={coverPageList?.coverpages ?? []}
          isLoading={isLoading}
          editCoverPage={editCoverPage}
          deleteCoverPage={setDeleteId}
          openAssignedUsersModal={openAssignedUsersModal}
          openAssignedNumbersModal={openAssignedNumbersModal}
        />
        {!isLoading && (
          <div className="mobile-pagination">
            <Pagination
              size="small"
              total={coverPageList?.total ?? 0}
              showSizeChanger={true}
              pageSize={contactfilter?.per_page ?? 10}
              current={contactfilter?.page ?? 1}
              onChange={(page, pageSize) => {
                setContactFilter({
                  ...contactfilter,
                  page: page,
                  per_page: pageSize,
                });
              }}
              onShowSizeChange={(page, pageSize) => {
                setContactFilter({
                  ...contactfilter,
                  page: page,
                  per_page: pageSize,
                });
              }}
              showTotal={(total, range) => `${range[1]}/ ${total}`}
            />
          </div>
        )}
      </div>
      <div className="mobile-table-block mobile-hide w-100">
        <Table
          columns={columns}
          data={
            coverPageList?.coverpages?.map((coverPage) => {
              return {
                ...coverPage,
                key: coverPage?.id,
              };
            }) ?? []
          }
          isLoading={isLoading}
          pagination={true}
          pageSize={contactfilter?.per_page}
          total={coverPageList?.total ?? 0}
          currentPage={contactfilter?.page}
          handlePageChange={handlePageChange}
          onRowClick={(record: CoverPageType) => {
            editCoverPage(record);
          }}
          rowClassName="clickable-rows"
        />
      </div>
      <ConfirmationModal
        isLoading={deleteLoading}
        open={!!deleteId}
        title="Are you sure you want to delete this cover page?"
        description="This action cannot be undone. Deleting the cover page will permanently remove all its content and cannot be recovered."
        onCancel={() => setDeleteId(null)}
        onConfirm={() => onDeleteCoverPage(deleteId)}
      />
      <AddCoverPageModal
        visible={isAddModalVisible}
        onCancelClick={() => setIsAddModalVisible(false)}
        onSuccess={() => onCoverPageListGet()}
      />
      {selectedCoverPage && (
        <EditCoverPageModal
          visible={isEditModalVisible}
          onClose={() => {
            setIsEditModalVisible(false);
            setSelectedCoverPage(null);
            onCoverPageListGet();
          }}
          coverPage={selectedCoverPage}
        />
      )}
      <AssignedUsersModal
        visible={isAssignedUsersModalVisible}
        onCancelClick={() => setIsAssignedUsersModalVisible(false)}
        assignedUsers={selectedAssignedUsers}
        coverPage={selectedCoverPage}
        onEditClick={() => {
          setIsAssignedUsersModalVisible(false);
          setIsEditModalVisible(true);
        }}
      />
      <AssignedNumbersModal
        visible={isAssignedNumbersModalVisible}
        onCancelClick={() => setIsAssignedNumbersModalVisible(false)}
        assignedNumbers={selectedAssignedNumbers}
        coverPage={selectedCoverPage}
        onEditClick={() => {
          setIsAssignedNumbersModalVisible(false);
          setIsEditModalVisible(true);
        }}
      />
    </FaxPageStyle>
  );
};

export default CoverPage;
